import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { TicketNumber } from '../components/TicketNumber';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Ticket, 
  Clock, 
  Trophy, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Sparkles,
  ArrowRight,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export default function MyTicketsPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [activeRaffle, setActiveRaffle] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [ticketsData, purchasesData, raffleData] = await Promise.all([
        api.getMyTickets(),
        api.getMyPurchases(),
        api.getActiveRaffle()
      ]);
      setTickets(ticketsData);
      setPurchases(purchasesData);
      setActiveRaffle(raffleData);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds to check for approved payments
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
            <CheckCircle className="w-3 h-3" />
            Confirmado
          </span>
        );
      case 'pending_qr':
      case 'pending_payment':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <Clock className="w-3 h-3" />
            Esperando pago
          </span>
        );
      case 'pending_approval':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            <Loader2 className="w-3 h-3 animate-spin" />
            Validando
          </span>
        );
      case 'failed':
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" />
            Rechazado
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
            {status}
          </span>
        );
    }
  };

  // Group tickets by raffle
  const ticketsByRaffle = tickets.reduce((acc, ticket) => {
    if (!acc[ticket.raffle_id]) {
      acc[ticket.raffle_id] = [];
    }
    acc[ticket.raffle_id].push(ticket);
    return acc;
  }, {});

  const activeTickets = activeRaffle ? ticketsByRaffle[activeRaffle.id] || [] : [];
  const pastTickets = Object.entries(ticketsByRaffle)
    .filter(([raffleId]) => raffleId !== activeRaffle?.id)
    .flatMap(([_, tix]) => tix);

  // Pending purchases
  const pendingPurchases = purchases.filter(p => 
    ['pending_qr', 'pending_payment', 'pending_approval'].includes(p.status)
  );

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="page-container pt-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="heading-2">Mis Tickets</h1>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={handleRefresh}
          disabled={refreshing}
          className="rounded-full"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Active Raffle Summary */}
      {activeRaffle && (activeRaffle.status === 'active' || activeRaffle.status === 'countdown') && (
        <Card className="card-base bg-gradient-to-br from-emerald-500 to-emerald-600 text-white" data-testid="active-raffle-summary">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-emerald-100">Sorteo Activo</span>
              <span className="bg-white/20 px-2 py-1 rounded-full text-xs">
                {activeRaffle.progress_percentage?.toFixed(1)}% vendido
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-100 mb-1">Tus tickets activos:</p>
                <p className="text-4xl font-bold">{activeTickets.length}</p>
              </div>
              
              {activeTickets.length > 0 ? (
                <div className="text-right">
                  <p className="text-xs text-emerald-100">Premio Mayor</p>
                  <p className="text-lg font-bold">{formatCurrency(1000000)}</p>
                </div>
              ) : (
                <Link to={`/buy/${activeRaffle.id}`}>
                  <Button className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Comprar
                  </Button>
                </Link>
              )}
            </div>
            
            {activeTickets.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-xs text-emerald-100 mb-2">Tus números:</p>
                <div className="flex flex-wrap gap-2">
                  {activeTickets.slice(0, 10).map((ticket) => (
                    <span 
                      key={ticket.id}
                      className="bg-white/20 px-3 py-1 rounded-full font-mono text-sm font-bold"
                    >
                      {ticket.number}
                    </span>
                  ))}
                  {activeTickets.length > 10 && (
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      +{activeTickets.length - 10} más
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending Payments Alert */}
      {pendingPurchases.length > 0 && (
        <Card className="card-base bg-amber-50 border-amber-200" data-testid="pending-payments-alert">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">
                  Tienes {pendingPurchases.length} compra{pendingPurchases.length > 1 ? 's' : ''} pendiente{pendingPurchases.length > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  {pendingPurchases.some(p => p.status === 'pending_approval') 
                    ? 'Tu pago está siendo validado. Los tickets se asignarán en minutos.'
                    : 'Completa el pago para recibir tus tickets.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Tickets */}
      {tickets.length === 0 && pendingPurchases.length === 0 && (
        <Card className="card-base text-center py-12" data-testid="no-tickets">
          <Ticket className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="heading-4 mb-2">No tienes tickets</h2>
          <p className="text-muted-foreground mb-4">
            Compra tickets para participar en el sorteo
          </p>
          {activeRaffle && (
            <Link to={`/buy/${activeRaffle.id}`}>
              <Button className="btn-primary rounded-full">
                <Sparkles className="w-4 h-4 mr-2" />
                Comprar Tickets
              </Button>
            </Link>
          )}
        </Card>
      )}

      {/* Tabs */}
      {(tickets.length > 0 || pendingPurchases.length > 0) && (
        <Tabs defaultValue="active" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-12 rounded-full bg-slate-100">
            <TabsTrigger value="active" className="rounded-full text-xs" data-testid="active-tickets-tab">
              <Ticket className="w-4 h-4 mr-1" />
              Activos ({activeTickets.length})
            </TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-full text-xs" data-testid="transactions-tab">
              <Clock className="w-4 h-4 mr-1" />
              Historial
            </TabsTrigger>
            <TabsTrigger value="past" className="rounded-full text-xs" data-testid="past-tickets-tab">
              <Trophy className="w-4 h-4 mr-1" />
              Anteriores
            </TabsTrigger>
          </TabsList>

          {/* Active Tickets */}
          <TabsContent value="active" className="mt-6 space-y-4">
            {activeTickets.length === 0 ? (
              <Card className="card-base text-center py-8">
                <Ticket className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  No tienes tickets en el sorteo activo
                </p>
                {activeRaffle && (
                  <Link to={`/buy/${activeRaffle.id}`}>
                    <Button variant="outline" className="rounded-full">
                      Comprar ahora
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                )}
              </Card>
            ) : (
              <Card className="card-base" data-testid="active-tickets-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-primary" />
                    {activeRaffle?.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {activeTickets.map((ticket) => (
                      <TicketNumber
                        key={ticket.id}
                        number={ticket.number}
                        isWinner={ticket.is_winner}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Transaction History */}
          <TabsContent value="transactions" className="mt-6 space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Historial de Transacciones</h3>
            
            {purchases.length === 0 ? (
              <Card className="card-base text-center py-8">
                <p className="text-muted-foreground">
                  No hay transacciones
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {purchases.map((purchase) => (
                  <Card key={purchase.id} className="card-base" data-testid={`transaction-${purchase.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium">
                            {purchase.total_tickets} ticket{purchase.total_tickets > 1 ? 's' : ''}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(purchase.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-primary">
                            {formatCurrency(purchase.amount_paid)}
                          </p>
                          {getStatusBadge(purchase.status)}
                        </div>
                      </div>
                      
                      {/* Show ticket numbers if completed */}
                      {purchase.status === 'completed' && purchase.ticket_numbers?.length > 0 && (
                        <div className="pt-3 border-t">
                          <p className="text-xs text-muted-foreground mb-2">Números asignados:</p>
                          <div className="flex flex-wrap gap-1">
                            {purchase.ticket_numbers.slice(0, 8).map((num, idx) => (
                              <span 
                                key={idx}
                                className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-mono text-xs"
                              >
                                {num}
                              </span>
                            ))}
                            {purchase.ticket_numbers.length > 8 && (
                              <span className="text-xs text-muted-foreground">
                                +{purchase.ticket_numbers.length - 8} más
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Show payment method for pending */}
                      {['pending_qr', 'pending_payment', 'pending_approval'].includes(purchase.status) && (
                        <div className="pt-3 border-t flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            Método: {purchase.payment_method === 'nequi' ? 'Bre-B' : purchase.payment_method === 'bancolombia' ? 'Bancolombia' : 'Bre-B'}
                          </span>
                          {purchase.payment_reference && (
                            <span className="text-xs font-mono bg-slate-100 px-2 py-1 rounded">
                              {purchase.payment_reference}
                            </span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Past Tickets */}
          <TabsContent value="past" className="mt-6 space-y-4">
            {pastTickets.length === 0 ? (
              <Card className="card-base text-center py-8">
                <p className="text-muted-foreground">
                  No tienes tickets en sorteos anteriores
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {pastTickets.map((ticket) => (
                  <Card key={ticket.id} className="card-base">
                    <CardContent className="p-4 flex items-center justify-between">
                      <TicketNumber number={ticket.number} isWinner={ticket.is_winner} />
                      {ticket.is_winner && (
                        <div className="flex items-center gap-1 text-amber-500">
                          <Trophy className="w-5 h-5" />
                          <span className="font-medium">Ganador</span>
                        </div>
                      )}
                      {ticket.is_inverted_winner && (
                        <div className="flex items-center gap-1 text-blue-500">
                          <Trophy className="w-5 h-5" />
                          <span className="font-medium">Invertido</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
